import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MapPin, LogOut, LayoutDashboard, Shield, ChevronDown, Menu, Plus, Search as SearchIcon, Tag, Wrench, UtensilsCrossed, Sparkles } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const go = (path) => { setMobileOpen(false); navigate(path); };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-emerald-900/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="logo-link">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-emerald-800 to-emerald-600 flex items-center justify-center text-white shadow-md flex-shrink-0">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-bold text-base sm:text-lg text-slate-900 truncate" style={{ fontFamily: "Outfit" }}>Apna<span className="text-emerald-700">Islamabad</span></div>
            <div className="hidden sm:block text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Local. Verified.</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
          <Link to="/search" className="hover:text-emerald-700" data-testid="nav-explore">Explore</Link>
          <Link to="/deals" className="hover:text-emerald-700" data-testid="nav-deals">Deals</Link>
          <Link to="/search?category=home_services" className="hover:text-emerald-700" data-testid="nav-home-services">Home Services</Link>
          <Link to="/search?category=food_restaurants" className="hover:text-emerald-700" data-testid="nav-food">Food</Link>
          <Link to="/search?category=salons_beauty" className="hover:text-emerald-700" data-testid="nav-salons">Salons</Link>
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          {!user ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login-btn" className="text-slate-700 px-2 sm:px-4 h-9">Log in</Button>
              <Button size="sm" onClick={() => navigate("/login?mode=signup")} data-testid="header-signup-btn" className="bg-emerald-800 hover:bg-emerald-900 text-white h-9 px-3 sm:px-4">
                <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">List your business</span><span className="sm:hidden text-xs font-semibold">List</span>
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-slate-200 hover:border-emerald-500 transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-800 text-xs font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium text-slate-800">{user.name?.split(" ")[0]}</span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> My Dashboard
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile hamburger */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-72">
              <SheetHeader><SheetTitle className="text-left">Menu</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-1">
                <button onClick={() => go("/search")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100" data-testid="mobile-nav-explore"><SearchIcon className="w-5 h-5 text-emerald-700" /> Explore all</button>
                <button onClick={() => go("/deals")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100" data-testid="mobile-nav-deals"><Tag className="w-5 h-5 text-emerald-700" /> Deals & offers</button>
                <div className="my-3 h-px bg-slate-200" />
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-3 mb-1">Categories</div>
                <button onClick={() => go("/search?category=home_services")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100"><Wrench className="w-5 h-5 text-emerald-700" /> Home Services</button>
                <button onClick={() => go("/search?category=food_restaurants")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100"><UtensilsCrossed className="w-5 h-5 text-emerald-700" /> Food & Restaurants</button>
                <button onClick={() => go("/search?category=salons_beauty")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100"><Sparkles className="w-5 h-5 text-emerald-700" /> Salons & Beauty</button>
                {user && (
                  <>
                    <div className="my-3 h-px bg-slate-200" />
                    <button onClick={() => go("/dashboard")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100"><LayoutDashboard className="w-5 h-5 text-emerald-700" /> My dashboard</button>
                    {user.role === "admin" && (
                      <button onClick={() => go("/admin")} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-slate-100"><Shield className="w-5 h-5 text-emerald-700" /> Admin panel</button>
                    )}
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
